import {
  MenuItem,
  MenuLinkItem,
} from '@affine/core/modules/app-sidebar/views/menu-item';
import { UiNavigationEntryProvider } from '@affine/core/modules/streamlain/copilot/ui-navigation-item';
import {
  EMPTY_NAVIGATION_ARGS,
  NavigationActivationError,
  type UiNavigationDefinition,
} from '@affine/core/modules/streamlain/copilot/workspace-navigation-catalog';
import type { WorkspaceViewContextService } from '@affine/core/modules/streamlain/copilot/workspace-view-context';
import { WorkspaceViewContextService as ContextService } from '@affine/core/modules/streamlain/copilot/workspace-view-context';
import { toggleAreaSidebar } from '@affine/core/modules/streamlain/native-app/area-sidebar';
import type { Workbench } from '@affine/core/modules/workbench/entities/workbench';
import type {
  NavigationArgs,
  WorkspaceNavigationArgsSchema,
  WorkspaceNavigationDescriptor,
  WorkspaceNavigationTarget,
  WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { LiveData, useService } from '@toeverything/infra';
import {
  createContext,
  type PropsWithChildren,
  type ReactElement,
  type ReactNode,
  type SVGAttributes,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import type { To } from 'react-router-dom';

export interface WorkspaceNavigationItemModel {
  key: string;
  label: string;
  definition: UiNavigationDefinition;
  /** The entry leads to what the active view shows. */
  active: () => boolean;
  /** Only route entries reopen or toggle an area sidebar; popovers own their toggle. */
  togglesAreaSidebar: boolean;
  render: (labelOverride?: string, postfix?: ReactElement) => ReactNode;
}
/** The ordinary UI list: adding an entry also adds its search and AI projection. */
export interface WorkspaceNavigationEntry {
  key: string;
  label: string;
  to?: To;
  icon?: ReactElement<SVGAttributes<SVGElement>>;
  keywords?: readonly string[];
  available?: () => boolean;
  capability?: WorkspaceNavigationDescriptor['capability'];
  argsSchema?: WorkspaceNavigationArgsSchema;
  activate?: (args: NavigationArgs) => void;
  beforeOpen?: () => void;
  reached?: () => boolean;
  target?:
    | Pick<WorkspaceNavigationTarget, 'dialog' | 'sidebar'>
    | (() => Pick<WorkspaceNavigationTarget, 'dialog' | 'sidebar'> | null);
  testId?: string;
  render?: (
    label: string,
    to: To | undefined,
    postfix?: ReactElement
  ) => ReactElement;
}
/** Stable AI definitions per entry key; handlers always read the latest entry. */
export type WorkspaceNavigationDefinitionCache = Map<
  string,
  {
    signature: string;
    latest: { current: WorkspaceNavigationEntry };
    definition: UiNavigationDefinition;
    reached: (route: string) => boolean;
    active: () => boolean;
  }
>;
function definitionSignature(
  scope: WorkspaceViewScope,
  entry: WorkspaceNavigationEntry,
  destination: string | undefined
) {
  return JSON.stringify([
    scope.workspaceId,
    scope.viewId,
    entry.label,
    entry.capability ?? null,
    entry.keywords ?? [],
    entry.argsSchema ?? null,
    destination ?? null,
    !!entry.activate,
  ]);
}
export function createWorkspaceNavigationItems(
  entries: readonly WorkspaceNavigationEntry[],
  owner: {
    scope: WorkspaceViewScope;
    workbench: Workbench;
    context: WorkspaceViewContextService;
  },
  cache: WorkspaceNavigationDefinitionCache = new Map()
): readonly WorkspaceNavigationItemModel[] {
  const { scope, workbench, context } = owner;
  const visible = entries.filter(entry => entry.available?.() ?? true);
  const keys = new Set(visible.map(entry => entry.key));
  for (const key of cache.keys()) if (!keys.has(key)) cache.delete(key);
  return visible.map(entry => {
    const destination =
      typeof entry.to === 'string' ? entry.to : entry.to?.pathname;
    const signature = definitionSignature(scope, entry, destination);
    let cached = cache.get(entry.key);
    if (cached?.signature === signature) cached.latest.current = entry;
    else {
      const latest = { current: entry };
      const path = destination?.split('?')[0].split('#')[0];
      const reached = (route: string) =>
        latest.current.reached
          ? latest.current.reached()
          : !!destination && route.split('?')[0].split('#')[0] === path;
      // Ordinary UI highlighting reads its owner state, never AI context.
      const active = () =>
        context.isActiveScope(scope) &&
        reached(workbench.activeView$.value.location$.value.pathname);
      const definition: UiNavigationDefinition = {
        id: entry.key,
        capability: entry.capability,
        label: entry.label,
        icon: entry.icon,
        keywords: entry.keywords ?? [],
        parentIds: [],
        argsSchema: entry.argsSchema ?? EMPTY_NAVIGATION_ARGS,
        available: () => latest.current.available?.() ?? true,
        activate: args => {
          const current = latest.current;
          if (!context.isActiveScope(scope))
            throw new NavigationActivationError('stale');
          if (!(current.available?.() ?? true))
            throw new NavigationActivationError('blocked');
          current.beforeOpen?.();
          if (current.to !== undefined)
            workbench.open(current.to, { show: false });
          else if (current.activate) current.activate(args);
          else throw new NavigationActivationError('blocked');
        },
        // The entry of the area on screen puts that area's sidebar away and brings it back.
        reselect: () => !!destination && active() && toggleAreaSidebar(),
        readTarget: () => {
          if (!context.isActiveScope(scope)) return null;
          const { target: read } = latest.current;
          const target = typeof read === 'function' ? read() : read;
          if (target === null) return null;
          const current = context.snapshot(scope);
          if (!current.ok) return null;
          const { snapshot } = current;
          if (!reached(snapshot.route)) return null;
          return {
            ...scope,
            revision: snapshot.revision,
            surfaceId: snapshot.surfaceId,
            route: snapshot.route,
            title: snapshot.title,
            sidebar: { side: 'left', entryId: entry.key },
            ...target,
          };
        },
      };
      cached = { signature, latest, definition, reached, active };
      cache.set(entry.key, cached);
    }
    const { definition, active } = cached;
    return {
      key: entry.key,
      label: entry.label,
      definition,
      active,
      togglesAreaSidebar: !!destination,
      render: (labelOverride?: string, postfix?: ReactElement) => {
        const label = labelOverride ?? entry.label;
        return (
          <UiNavigationEntryProvider
            scope={scope}
            ownerId="workspace-navigation"
            definition={definition}
          >
            {entry.render ? (
              entry.render(label, entry.to, postfix)
            ) : entry.to !== undefined ? (
              <MenuLinkItem
                to={entry.to}
                icon={entry.icon}
                active={active()}
                postfix={postfix}
                data-testid={entry.testId}
              >
                {label}
              </MenuLinkItem>
            ) : (
              <MenuItem
                icon={entry.icon}
                active={active()}
                postfix={postfix}
                data-testid={entry.testId}
              >
                {label}
              </MenuItem>
            )}
          </UiNavigationEntryProvider>
        );
      },
    };
  });
}
/** Owner hook: re-renders keep definition identity unless the descriptor changes. */
export function useWorkspaceNavigationModels(
  entries: readonly WorkspaceNavigationEntry[],
  owner: Parameters<typeof createWorkspaceNavigationItems>[1]
): readonly WorkspaceNavigationItemModel[] {
  const cache = useRef<WorkspaceNavigationDefinitionCache>(new Map()).current;
  return createWorkspaceNavigationItems(entries, owner, cache);
}
const WorkspaceNavigationItemsContext = createContext<{
  scope: WorkspaceViewScope;
  items: readonly WorkspaceNavigationItemModel[];
} | null>(null);
/** One layout owner, shared by the rail and independently rendered document panes. */
export function WorkspaceNavigationItemsProvider({
  scope,
  items,
  children,
}: PropsWithChildren<{
  scope: WorkspaceViewScope;
  items: readonly WorkspaceNavigationItemModel[];
}>) {
  const context = useService(ContextService);
  const source$ = useMemo(
    () => new LiveData<readonly UiNavigationDefinition[]>([]),
    []
  );
  useLayoutEffect(
    () =>
      context.navigation.registerSource({
        scope,
        ownerId: 'workspace-navigation',
        items$: source$,
      }),
    [context, scope, source$]
  );
  // Publish only real definition changes; re-renders must not stale running intents.
  useLayoutEffect(() => {
    const definitions = items.map(item => item.definition);
    const previous = source$.value;
    if (
      previous.length === definitions.length &&
      previous.every((definition, index) => definition === definitions[index])
    )
      return;
    source$.next(definitions);
  }, [items, source$]);
  const value = useMemo(() => ({ scope, items }), [scope, items]);
  return (
    <WorkspaceNavigationItemsContext.Provider value={value}>
      {children}
    </WorkspaceNavigationItemsContext.Provider>
  );
}
export function useWorkspaceNavigationItems(
  scope: WorkspaceViewScope
): readonly WorkspaceNavigationItemModel[] {
  const value = useContext(WorkspaceNavigationItemsContext);
  if (!value) throw new Error('Workspace navigation requires its layout owner');
  return value.scope.workspaceId === scope.workspaceId &&
    value.scope.viewId === scope.viewId
    ? value.items
    : [];
}
